import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowLeft, Storefront } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function Success() {
    const { t } = useI18n();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [info, setInfo] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("missing");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                setInfo(r.data);
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (attempts >= 10) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                if (attempts >= 10) {
                    setStatus("error");
                    return;
                }
                setTimeout(poll, 2000);
            }
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background grid place-items-center px-4">
            <div
                data-testid="success-card"
                className="max-w-lg w-full bg-card border border-border rounded-3xl p-10 text-center"
            >
                {status === "checking" && (
                    <>
                        <div className="mx-auto h-16 w-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                        <h1 className="font-display font-black text-3xl mt-6">Confirming your order...</h1>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle size={72} weight="fill" className="text-emerald-500 mx-auto" />
                        <h1 className="font-display font-black text-4xl mt-6">{t("success.title")}</h1>
                        <p className="text-muted-foreground mt-3">{t("success.body")}</p>
                        {info && (
                            <p className="mt-4 font-display font-black text-2xl">
                                ${(info.amount_total / 100).toFixed(2)}
                            </p>
                        )}
                        {info?.order_id && (
                            <Link
                                to={`/order/${info.order_id}`}
                                data-testid="success-track-btn"
                                className="mt-6 inline-flex items-center gap-2 bg-foreground text-background rounded-full px-6 py-3 font-bold uppercase tracking-widest text-sm hover:opacity-90 transition"
                            >
                                <Storefront size={16} weight="bold" />
                                {t("success.track") || "Track my order"}
                            </Link>
                        )}
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error" || status === "missing") && (
                    <>
                        <Storefront size={64} weight="duotone" className="text-primary mx-auto" />
                        <h1 className="font-display font-black text-3xl mt-6">Order pending</h1>
                        <p className="text-muted-foreground mt-3">
                            We couldn't confirm your payment. If you were charged, please call (203) 466-6226.
                        </p>
                    </>
                )}
                <Link
                    to="/"
                    data-testid="back-home"
                    className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-3 font-bold uppercase tracking-widest text-sm hover:-translate-y-0.5 transition"
                >
                    <ArrowLeft size={16} weight="bold" />
                    Back to menu
                </Link>
            </div>
        </div>
    );
}
